import React, { useState } from 'react'
import { connect } from 'react-redux'
import { Row, Button, Badge } from 'reactstrap'
import BootstrapTable from 'react-bootstrap-table-next'
import paginationFactory from 'react-bootstrap-table2-paginator'
import filterFactory, { selectFilter, textFilter } from 'react-bootstrap-table2-filter'
import { uniqBy } from 'lodash'
import Moment from 'react-moment'
import { handleApproveMultipleChanges } from '../actions/approvalActions'
import useFetchingApprovalList from './useFetchingApprovalList'
import ActionCell from './ActionCell'
import LoadingSpinner from '../Shared/LoadingSpinner'
import PageContainer from '../Shared/LayoutComponents/PageContainer'
import HeaderRow from '../Shared/LayoutComponents/HeaderRow'
import PageHeading from '../Shared/LayoutComponents/PageHeading'

const Approvals = (props) => {
  let enabledFilter = null
  let companyFilter = null
  let statusFilter = null
  const [selected, setSelected] = useState([])

  const selectEnabledOptions = [
    { value: true, label: 'True' },
    { value: false, label: 'False' },
  ]

  const dateFormatter = (cell, row, rowIndex, formatExtraData) => {
    return <Moment format="DD/MM/YYYY">{cell}</Moment>
  }

  const dateIgnoreFormatter = (cell, row, rowIndex, formatExtraData) => {
    if (row.invoiceStatus === 'Ignored') {
      if (cell === null || cell === undefined) {
        return <span>No End Date Set</span>
      }
      return <Moment format="DD/MM/YYYY">{cell}</Moment>
    } else {
      return null
    }
  }

  const actionFormatter = (cell, row, rowIndex, formatExtraData) => {
    return <ActionCell cell={cell} row={row} rowIndex={rowIndex} formatExtraData={formatExtraData} />
  }

  const changeLogFormatter = (cell, row, rowIndex, formatExtraData) => {
    let lines = cell.split(';')
    return (
      <ul>
        {lines.map((line, index) => (
          <li key={index}>{line}</li>
        ))}
      </ul>
    )
  }

  const handleOnSelect = (row, isSelect) => {
    if (isSelect) {
      let newSelect = [...selected, row.id]
      setSelected(newSelect)
    } else {
      let newSelect = selected.filter((x) => x !== row.id)
      setSelected(newSelect)
    }
  }

  const handleOnSelectAll = (isSelect, rows) => {
    const ids = rows.map((r) => r.id)
    if (isSelect) {
      setSelected(ids)
    } else {
      setSelected([])
    }
  }

  const selectRow = {
    mode: 'checkbox',
    selected: selected,
    onSelect: handleOnSelect,
    onSelectAll: handleOnSelectAll,
  }

  const columns = [
    {
      dataField: 'id',
      text: 'Id',
      hidden: true,
      editable: false,
      csvExport: false,
    },
    {
      dataField: 'companyName',
      text: 'Company',
      sort: true,
      filter: selectFilter({
        placeholder: 'Filter...',
        options: uniqBy(props.approvalList, 'companyName').map((company) => {
          return {
            value: company.companyName,
            label: company.companyName,
          }
        }),
        getFilter: (filter) => {
          companyFilter = filter
        },
      }),
      headerStyle: (column, colIndex) => {
        return { width: '175px' }
      },
      editable: false,
    },
    {
      dataField: 'displayName',
      text: 'Name',
      sort: true,
      filter: textFilter({
        placeholder: 'Filter Name...',
      }),
      headerStyle: (column, colIndex) => {
        return { width: '175px' }
      },
      editable: false,
    },
    {
      dataField: 'accountEnabled',
      text: 'Account Enabled',
      sort: true,
      editable: false,
      filter: selectFilter({
        placeholder: 'Filter...',
        options: selectEnabledOptions,
        getFilter: (filter) => {
          enabledFilter = filter
        },
      }),
      headerStyle: (column, colIndex) => {
        return { width: '125px' }
      },
    },
    {
      dataField: 'invoiceStatus',
      text: 'Status',
      sort: true,
      editable: false,
      filter: selectFilter({
        placeholder: 'Filter...',
        options: selectEnabledOptions,
        getFilter: (filter) => {
          statusFilter = filter
        },
      }),
      headerStyle: (column, colIndex) => {
        return { width: '125px' }
      },
    },
    {
      dataField: 'ignoreUntil',
      formatter: dateIgnoreFormatter,
      text: 'Ignored Until',
      sort: true,
      editable: false,
      headerStyle: (column, colIndex) => {
        return { width: '125px' }
      },
    },
    {
      dataField: 'changeLog.lastChange',
      formatter: dateFormatter,
      text: 'Last Change',
      sort: true,
      editable: false,
      headerStyle: (column, colIndex) => {
        return { width: '125px' }
      },
    },
    {
      dataField: 'changeLog.changeDescriptions',
      text: 'Change Log',
      formatter: changeLogFormatter,
      sort: true,
      filter: textFilter({
        placeholder: 'Filter Email...',
      }),
      editable: false,
    },
    {
      dataField: 'actions',
      text: 'Actions',
      editable: false,
      formatter: actionFormatter,
      headerStyle: (column, colIndex) => {
        return { width: '175px' }
      },
    },
  ]

  useFetchingApprovalList()

  if (props.loading) {
    return <LoadingSpinner />
  } else if (props.approvalList && props.approvalList.length > 0) {
    return (
      <PageContainer>
        <Row>
          <HeaderRow>
            <PageHeading>Approval Required Changes</PageHeading>
          </HeaderRow>
        </Row>
        <HeaderRow>
          <Button
            onClick={() => props.handleApproveMultipleChanges(selected)}
            color="success"
            disabled={selected.length > 0 ? false : true}
          >
            Approve Changes <Badge color="dark">{selected.length}</Badge>
          </Button>
        </HeaderRow>
        {props.approvalLoading ? (
          <LoadingSpinner />
        ) : (
          <BootstrapTable
            bordered={false}
            keyField="id"
            bootstrap4
            filterPosition="top"
            columns={columns}
            data={props.approvalList}
            {...props.baseProps}
            pagination={paginationFactory()}
            filter={filterFactory()}
            selectRow={selectRow}
            loading={props.approvalLoading}
            remote={{ cellEdit: true }}
          />
        )}
      </PageContainer>
    )
  } else {
    return (
      <PageContainer>
        <Row>
          <HeaderRow>
            <PageHeading>Approval Required</PageHeading>
          </HeaderRow>
        </Row>
        <div>There are currently no pending approvals.</div>
      </PageContainer>
    )
  }
}

const mapStateToProps = (state) => ({
  loading: state.approval.loading,
  approvalLoading: state.approval.approvalLoading,
  approvalList: state.approval.approvalList,
})

const mapDispatchToProps = {
  handleApproveMultipleChanges,
}

export default connect(mapStateToProps, mapDispatchToProps)(Approvals)
